import DayjsUtils from '@date-io/dayjs';
import { FormControl, FormLabel, FormHelperText, InputAdornment, IconButton } from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { MuiPickersUtilsProvider, TimePicker, TimePickerProps } from '@material-ui/pickers';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import useCommonStyles from '../../useCommonStyles';

export interface OutlinedTimePickerFieldProps extends Omit<TimePickerProps, 'value' | 'onChange'> {
    name: string;
}

const defaultMaterialTheme = createTheme({
    palette: {
        primary: {
            main: '#333333',
            contrastText: '#fff',
        },
        secondary: {
            main: '#333333',
            contrastText: '#fff',
        },
    },
    typography: {
        fontFamily: 'AudiTypeVar-Normal, Verdana, sans-serif !important',
    },
});

const OutlinedTimePickerField = ({ name, label, ...props }: OutlinedTimePickerFieldProps) => {
    const { t } = useTranslation('common');
    const { disabled } = props;
    const [field, meta] = useField({ name });
    const { setFieldValue } = useFormikContext();
    const commonStyles = useCommonStyles();

    const hasError = !!meta.error && meta.touched;

    return (
        <FormControl fullWidth>
            {label && (
                <FormLabel className={commonStyles.formLabel} disabled={disabled}>
                    {label}
                </FormLabel>
            )}
            <ThemeProvider theme={defaultMaterialTheme}>
                <MuiPickersUtilsProvider utils={DayjsUtils}>
                    <TimePicker
                        // by default auto complete is off but props can always override it
                        autoComplete="off"
                        // spread props
                        {...props}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton>
                                        <AccessTimeIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        // the error is defined from what we go in meta data
                        error={hasError}
                        // date format
                        format={t('common:formats.timePickerFormat')}
                        inputVariant="outlined"
                        onChange={value => setFieldValue(name, value.toDate())}
                        value={field.value}
                    />
                </MuiPickersUtilsProvider>
            </ThemeProvider>
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default OutlinedTimePickerField;
